/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { Fragment, useEffect, useLayoutEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { signOut } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { useStore } from "../../contexts/StoreContext";
import { useLanguage } from "../../contexts/LanguageContext";
import LanguageSelector from "../../components/LanguageSelector";
import ScannerOnlyNotice from "../../components/ScannerOnlyNotice";
import logoDark from "../../assets/logo-vertical-dark.png";
import { safeStorage, safeSessionStorage } from "../../utils/storage";
import { clearStartFlow, markStartFlow } from "../../utils/onboardingFlow";
import { identifyStartFlowUser, openSupportChat, track } from "../../utils/analytics";
import { getBrowserContext } from "../../utils/browser";
import { StartDataProvider, useStartData } from "./StartContext";
import { returnedFromCheckout } from "./checkout";
import { FlyerJobProvider } from "./FlyerJob";
import { ErrorBox, FullPageSpinner, cx } from "./ui";
import StartSignup from "./StartSignup";
import Customize from "./Customize";
import TryIt from "./TryIt";
import GoLive from "./GoLive";
import Done from "./Done";

const STEPS = [
  { id: "customize", path: "/start" },
  { id: "try", path: "/start/try" },
  { id: "plan", path: "/start/plan" },
  { id: "done", path: "/start/done" },
];

// Page chrome while /start is mounted: normal line height, no Intercom
// launcher or version toast (see index.css), and the viewport may extend under
// the iPhone home indicator so the sticky button can pad for it.
function usePageChrome() {
  useEffect(() => {
    document.body.classList.add("start-flow");
    const meta = document.querySelector('meta[name="viewport"]');
    const original = meta?.getAttribute("content");
    if (meta && original && !/viewport-fit/.test(original)) {
      meta.setAttribute("content", `${original}, viewport-fit=cover`);
    }
    return () => {
      document.body.classList.remove("start-flow");
      if (meta && original) meta.setAttribute("content", original);
    };
  }, []);
}

function Progress({ current }) {
  const { t } = useLanguage();
  const index = STEPS.findIndex((s) => s.id === current);
  if (index < 0) return null;
  return (
    <nav aria-label={t("start.shell.progressLabel")} className="mx-auto w-full max-w-xl px-4 pb-3 sm:px-6">
      <ol className="grid grid-cols-4 gap-2">
        {STEPS.map((step, i) => (
          <li key={step.id} aria-current={i === index ? "step" : undefined}>
            <div className={cx("h-1.5 rounded-full", i <= index ? "bg-[#4522ED]" : "bg-gray-200")} />
            <span
              className={cx(
                "mt-1.5 block truncate text-xs",
                i === index ? "font-semibold text-gray-900" : "text-gray-500",
              )}
            >
              {t(`start.shell.steps.${step.id}`)}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Header({ step }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleHelp = () => {
    track("start_help_opened", { step: step || "signup" });
    if (!openSupportChat()) window.location.href = "mailto:support@loyaltify.io";
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } finally {
      safeStorage.clear();
      safeSessionStorage.clear();
      // Still on /start: keep this browser in the new flow.
      markStartFlow();
      navigate("/start", { replace: true });
    }
  };

  return (
    <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-14 w-full max-w-xl items-center justify-between gap-2 px-4 sm:px-6">
        <img src={logoDark} alt="Loyaltify" className="h-7 w-auto" />
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleHelp}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 0 1-4-.83L3 20l1.4-3.72A7.38 7.38 0 0 1 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8Z" />
            </svg>
            {t("start.shell.help")}
          </button>
          <div className="flex min-h-11 items-center px-1">
            <LanguageSelector />
          </div>
          {user && (
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label={t("start.shell.accountMenu")}
                className="flex h-11 w-11 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4" />
                  <path strokeLinecap="round" d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
                </svg>
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-40 mt-2 w-56 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                  <div className="truncate px-4 py-2 text-sm text-gray-500">{user.email}</div>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className={cx("block w-full px-4 py-3 text-left text-sm font-medium text-gray-900", active && "bg-gray-100")}
                      >
                        {t("start.shell.signOut")}
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          )}
        </div>
      </div>
      <Progress current={step} />
    </header>
  );
}

export function StartPage({ step, children }) {
  return (
    <div className="min-h-screen bg-[#F9FAFB] text-gray-900" style={{ lineHeight: 1.5 }}>
      <Header step={step} />
      <main className="mx-auto w-full max-w-xl px-4 pb-6 pt-4 sm:px-6">{children}</main>
    </div>
  );
}

// Someone whose store was already set up outside /start (an existing
// customer who tapped the ad and signed in) has nothing to do here. Their
// browser is unmarked again so the dashboard's Setup link opens /setup for
// them as before.
function LeaveStartFlow() {
  useEffect(() => {
    clearStartFlow();
  }, []);
  return <Navigate to="/dashboard" replace />;
}

// Sends each step to the right place for what already exists on the server.
function StepGate({ step, children }) {
  const { t } = useLanguage();
  const location = useLocation();
  const { storeId, status, startPass, initForm, isLoading, error, refresh } = useStartData();
  // Once a step is on screen it stays mounted while its data reloads (e.g.
  // when Customize creates the store and every query restarts under the new
  // id). Unmounting it would drop focus, a logo being picked, or a save in
  // progress.
  const shown = useRef(false);

  if (storeId && isLoading) {
    return shown.current ? children : <FullPageSpinner label={t("start.shell.loading")} />;
  }
  if (storeId && error && !status) {
    return (
      <ErrorBox onRetry={refresh} retryLabel={t("start.shell.retry")}>
        {t("start.shell.loadError")}
      </ErrorBox>
    );
  }

  const hasCard = !!(startPass && initForm);
  const madeByStart = startPass?.pass?.onboarding?.flow === "start";
  const returned = returnedFromCheckout(location.search);
  if (status?.setup_completed && !(hasCard && madeByStart)) return <LeaveStartFlow />;
  // Someone who has paid (or just came back from Stripe while the webhook is
  // on its way) belongs on Done, not back at the start of the flow.
  if (step === "customize" && (status?.setup_completed || (hasCard && (status?.has_subscription || returned)))) {
    return <Navigate to="/start/done" replace />;
  }
  if ((step === "try" || step === "plan" || step === "done") && !hasCard) {
    return <Navigate to="/start" replace />;
  }
  // Never offer a second checkout while the first one's webhook is pending.
  if (step === "plan" && (status?.has_subscription || returned)) return <Navigate to="/start/done" replace />;
  if (step === "done" && !status?.has_subscription && !returned) {
    return <Navigate to="/start/plan" replace />;
  }
  shown.current = true;
  return children;
}

function AuthenticatedFlow() {
  const { t } = useLanguage();
  const { isLoading, isScannerOnly, canManageStore, selectedStore, stores } = useStore();
  const queryClient = useQueryClient();

  if (isLoading) return <FullPageSpinner label={t("start.shell.loading")} />;
  // The store list failed to load (a dropped request in an in-app browser
  // after a reload). It must not read as "no store yet", or Customize would
  // create a second one.
  if (!stores) {
    return (
      <StartPage step={null}>
        <ErrorBox onRetry={() => queryClient.refetchQueries({ queryKey: ["stores"] })} retryLabel={t("start.shell.retry")}>
          {t("start.shell.loadError")}
        </ErrorBox>
      </StartPage>
    );
  }
  if (isScannerOnly) return <ScannerOnlyNotice />;
  // Team members cannot set up a store; the dashboard is where they belong.
  if (selectedStore && !canManageStore) return <Navigate to="/dashboard" replace />;

  return (
    <StartDataProvider>
      <FlyerJobProvider>
      <Routes>
        <Route
          index
          element={
            <StartPage step="customize">
              <StepGate step="customize">
                <Customize />
              </StepGate>
            </StartPage>
          }
        />
        <Route
          path="try"
          element={
            <StartPage step="try">
              <StepGate step="try">
                <TryIt />
              </StepGate>
            </StartPage>
          }
        />
        <Route
          path="plan"
          element={
            <StartPage step="plan">
              <StepGate step="plan">
                <GoLive />
              </StepGate>
            </StartPage>
          }
        />
        <Route
          path="done"
          element={
            <StartPage step="done">
              <StepGate step="done">
                <Done />
              </StepGate>
            </StartPage>
          }
        />
        <Route path="*" element={<Navigate to="/start" replace />} />
      </Routes>
      </FlyerJobProvider>
    </StartDataProvider>
  );
}

export default function StartFlow() {
  const { user, loading } = useAuth();
  usePageChrome();

  // A layout effect, so the mark lands before any step's own effects run:
  // LeaveStartFlow, which can render on the very first pass when the setup
  // status is already cached, must be the one that has the last word.
  useLayoutEffect(() => {
    markStartFlow();
  }, []);

  useEffect(() => {
    const { platform, inAppBrowser } = getBrowserContext();
    track("start_opened", { platform, in_app_browser: inAppBrowser || "none" });
  }, []);

  useEffect(() => {
    if (user) identifyStartFlowUser();
  }, [user]);

  if (loading) return <FullPageSpinner />;
  if (!user) {
    return (
      <StartPage step={null}>
        <StartSignup />
      </StartPage>
    );
  }
  return <AuthenticatedFlow />;
}
